import { useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";

import { isAdminRole } from "@/auth/shared";
import { Alert } from "@/components/base/alert";
import { Button } from "@/components/base/button";
import { addToast } from "@/components/base/toast";
import { DefaultLayout } from "@/layouts/default";

export const Route = createFileRoute("/admin/system-tasks")({
  loader: async ({ context: { queryClient, ...context } }) => {
    const viewer = context.viewer;

    if (!viewer || !isAdminRole(viewer.role)) {
      throw redirect({ to: "/not-found" });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const {
    mutate: rollupRatings,
    isPending: isRollupRatingsPending,
    failureReason: rollupRatingsFailureReason,
  } = useMutation({
    mutationFn: async () => {
      const response = await fetch("/api/tasks/ratings/rollup", {
        method: "GET",
      });

      if (!response.ok) {
        const data = await response.json();

        throw new Error(data.error ?? "Failed to rollup ratings");
      }

      addToast({
        variant: "success",
        title: "Success",
        description: "Completed ratings and points rollups.",
      });

      return response.json();
    },
  });

  const {
    mutate: vblPublish,
    isPending: isVblPublishPending,
    failureReason: vblPublishFailureReason,
  } = useMutation({
    mutationFn: async () => {
      const response = await fetch("/api/tasks/vbl/publish", {
        method: "GET",
      });

      if (!response.ok) {
        const data = await response.json();

        throw new Error(data.error ?? "Failed to publish to VBL");
      }

      const data = await response.json();

      addToast({
        variant: "success",
        title: "Success",
        description: `Published ${data.published} tournament(s) to VBL.`,
      });

      return data;
    },
  });

  const {
    mutate: teamDropTdEmails,
    isPending: isTeamDropTdEmailsPending,
    failureReason: teamDropTdEmailsFailureReason,
  } = useMutation({
    mutationFn: async () => {
      const response = await fetch("/api/tasks/emails/team-drop-td", {
        method: "GET",
      });

      if (!response.ok) {
        const data = await response.json();

        throw new Error(data.error ?? "Failed to process team drop TD emails");
      }

      const data = await response.json();

      addToast({
        variant: "success",
        title: "Success",
        description: `Sent ${data.sent} email(s) for ${data.processed} event(s).`,
      });

      return data;
    },
  });

  const {
    mutate: withdrawRefunds,
    isPending: isWithdrawRefundsPending,
    failureReason: withdrawRefundsFailureReason,
  } = useMutation({
    mutationFn: async () => {
      const response = await fetch(
        "/api/tasks/refunds/process-withdraw-refunds",
        { method: "GET" },
      );

      if (!response.ok) {
        const data = await response.json();

        throw new Error(data.error ?? "Failed to process withdraw refunds");
      }

      const data = await response.json();

      addToast({
        variant: "success",
        title: "Success",
        description: `Refunded ${data.refunded} of ${data.claimed} withdraw event(s) for $${data.totalRefunded}.`,
      });

      return data;
    },
  });

  const {
    mutate: waitlistRefunds,
    isPending: isWaitlistRefundsPending,
    failureReason: waitlistRefundsFailureReason,
  } = useMutation({
    mutationFn: async () => {
      const response = await fetch(
        "/api/tasks/refunds/process-completed-divisions",
        { method: "GET" },
      );

      if (!response.ok) {
        const data = await response.json();

        throw new Error(data.error ?? "Failed to process waitlist refunds");
      }

      const data = await response.json();

      addToast({
        variant: "success",
        title: "Success",
        description: `Refunded ${data.refundedCount} waitlisted team(s) for $${data.totalRefunded}.`,
      });

      return data;
    },
  });

  const {
    mutate: removedTeamEmails,
    isPending: isRemovedTeamEmailsPending,
    failureReason: removedTeamEmailsFailureReason,
  } = useMutation({
    mutationFn: async () => {
      const response = await fetch("/api/tasks/emails/removed-team", {
        method: "GET",
      });

      if (!response.ok) {
        const data = await response.json();

        throw new Error(data.error ?? "Failed to process removed team emails");
      }

      const data = await response.json();

      addToast({
        variant: "success",
        title: "Success",
        description: `Sent ${data.sent} of ${data.processed} removed-team email(s).`,
      });

      return data;
    },
  });

  const {
    mutate: addedToTeamEmails,
    isPending: isAddedToTeamEmailsPending,
    failureReason: addedToTeamEmailsFailureReason,
  } = useMutation({
    mutationFn: async () => {
      const response = await fetch("/api/tasks/emails/added-to-team", {
        method: "GET",
      });

      if (!response.ok) {
        const data = await response.json();

        throw new Error(data.error ?? "Failed to process added-to-team emails");
      }

      return response.json();
    },
    onSuccess: (data) => {
      addToast({
        variant: "success",
        title: "Success",
        description: `Sent ${data.sent} of ${data.processed} added-to-team email(s).`,
      });
    },
  });

  const {
    mutate: waitlistRefundEmails,
    isPending: isWaitlistRefundEmailsPending,
    failureReason: waitlistRefundEmailsFailureReason,
  } = useMutation({
    mutationFn: async () => {
      const response = await fetch("/api/tasks/emails/waitlist-refund", {
        method: "GET",
      });

      if (!response.ok) {
        const data = await response.json();

        throw new Error(
          data.error ?? "Failed to process waitlist refund emails",
        );
      }

      return response.json();
    },
    onSuccess: (data) => {
      addToast({
        variant: "success",
        title: "Success",
        description: `Sent ${data.sent} of ${data.processed} waitlist-refund email(s).`,
      });
    },
  });

  const {
    mutate: refundAdminAlertEmails,
    isPending: isRefundAdminAlertEmailsPending,
    failureReason: refundAdminAlertEmailsFailureReason,
  } = useMutation({
    mutationFn: async () => {
      const response = await fetch("/api/tasks/emails/refund-admin-alert", {
        method: "GET",
      });

      if (!response.ok) {
        const data = await response.json();

        throw new Error(data.error ?? "Failed to process refund admin alerts");
      }

      return response.json();
    },
    onSuccess: (data) => {
      addToast({
        variant: "success",
        title: "Success",
        description: `Sent ${data.sent} of ${data.processed} refund admin alert(s).`,
      });
    },
  });

  const {
    mutate: processTransfers,
    isPending: isProcessTransfersPending,
    failureReason: processTransfersFailureReason,
  } = useMutation({
    mutationFn: async () => {
      const response = await fetch("/api/tasks/transfers/process", {
        method: "GET",
      });

      if (!response.ok) {
        const data = await response.json();

        throw new Error(data.error ?? "Failed to process team transfers");
      }

      return response.json();
    },
    onSuccess: (data) => {
      addToast({
        variant: "success",
        title: "Success",
        description: `Processed ${data.claimed} transfer(s): ${data.results?.length ?? 0} succeeded, ${data.errors?.length ?? 0} failed.`,
      });
    },
  });

  const {
    mutate: generateInvites,
    isPending: isGenerateInvitesPending,
    failureReason: generateInvitesFailureReason,
  } = useMutation({
    mutationFn: async () => {
      const response = await fetch("/api/tasks/invites/generate", {
        method: "GET",
      });

      if (!response.ok) {
        const data = await response.json();

        throw new Error(data.error ?? "Failed to generate invites");
      }

      return response.json();
    },
    onSuccess: (data) => {
      addToast({
        variant: "success",
        title: "Success",
        description: `Processed ${data.sourcesProcessed} source(s), generated ${data.invitesGenerated} invite(s).`,
      });
    },
  });

  const {
    mutate: cleanupStorage,
    isPending: isCleanupStoragePending,
    failureReason: cleanupStorageFailureReason,
  } = useMutation({
    mutationFn: async () => {
      const response = await fetch("/api/tasks/cleanup/storage", {
        method: "POST",
      });

      if (!response.ok) {
        const data = await response.json();

        throw new Error(data.error ?? "Failed to cleanup storage");
      }

      addToast({
        variant: "success",
        title: "Success",
        description: "Completed storage cleanup.",
      });

      return response.json();
    },
  });

  return (
    <DefaultLayout
      classNames={{
        content: "flex flex-col space-y-12 max-w-2xl px-3 py-12 mx-auto",
      }}
    >
      <div className="flex flex-col">
        {rollupRatingsFailureReason && (
          <Alert
            color="error"
            title="Uh oh."
            description={rollupRatingsFailureReason.message}
          />
        )}
        <Button
          color="primary"
          onPress={() => rollupRatings()}
          isDisabled={isRollupRatingsPending}
        >
          Rollup Ratings
        </Button>
      </div>

      <div className="flex flex-col">
        {vblPublishFailureReason && (
          <Alert
            color="error"
            title="Uh oh."
            description={vblPublishFailureReason.message}
          />
        )}
        <Button
          color="primary"
          onPress={() => vblPublish()}
          isDisabled={isVblPublishPending}
        >
          Publish to VBL
        </Button>
      </div>

      <div className="flex flex-col">
        {teamDropTdEmailsFailureReason && (
          <Alert
            color="error"
            title="Uh oh."
            description={teamDropTdEmailsFailureReason.message}
          />
        )}
        <Button
          color="primary"
          onPress={() => teamDropTdEmails()}
          isDisabled={isTeamDropTdEmailsPending}
        >
          Process team drop TD emails
        </Button>
      </div>

      <div className="flex flex-col">
        {withdrawRefundsFailureReason && (
          <Alert
            color="error"
            title="Uh oh."
            description={withdrawRefundsFailureReason.message}
          />
        )}
        <Button
          color="primary"
          onPress={() => withdrawRefunds()}
          isDisabled={isWithdrawRefundsPending}
        >
          Process withdraw refunds
        </Button>
      </div>

      <div className="flex flex-col">
        {waitlistRefundsFailureReason && (
          <Alert
            color="error"
            title="Uh oh."
            description={waitlistRefundsFailureReason.message}
          />
        )}
        <Button
          color="primary"
          onPress={() => waitlistRefunds()}
          isDisabled={isWaitlistRefundsPending}
        >
          Process waitlist refunds
        </Button>
      </div>

      <div className="flex flex-col">
        {removedTeamEmailsFailureReason && (
          <Alert
            color="error"
            title="Uh oh."
            description={removedTeamEmailsFailureReason.message}
          />
        )}
        <Button
          color="primary"
          onPress={() => removedTeamEmails()}
          isDisabled={isRemovedTeamEmailsPending}
        >
          Process removed team emails
        </Button>
      </div>

      <div className="flex flex-col">
        {addedToTeamEmailsFailureReason && (
          <Alert
            color="error"
            title="Uh oh."
            description={addedToTeamEmailsFailureReason.message}
          />
        )}
        <Button
          color="primary"
          onPress={() => addedToTeamEmails()}
          isDisabled={isAddedToTeamEmailsPending}
        >
          Process added-to-team emails
        </Button>
      </div>

      <div className="flex flex-col">
        {waitlistRefundEmailsFailureReason && (
          <Alert
            color="error"
            title="Uh oh."
            description={waitlistRefundEmailsFailureReason.message}
          />
        )}
        <Button
          color="primary"
          onPress={() => waitlistRefundEmails()}
          isDisabled={isWaitlistRefundEmailsPending}
        >
          Process waitlist refund emails
        </Button>
      </div>

      <div className="flex flex-col">
        {refundAdminAlertEmailsFailureReason && (
          <Alert
            color="error"
            title="Uh oh."
            description={refundAdminAlertEmailsFailureReason.message}
          />
        )}
        <Button
          color="primary"
          onPress={() => refundAdminAlertEmails()}
          isDisabled={isRefundAdminAlertEmailsPending}
        >
          Process refund admin alerts
        </Button>
      </div>

      <div className="flex flex-col">
        {processTransfersFailureReason && (
          <Alert
            color="error"
            title="Uh oh."
            description={processTransfersFailureReason.message}
          />
        )}
        <Button
          color="primary"
          onPress={() => processTransfers()}
          isDisabled={isProcessTransfersPending}
        >
          Process team transfers
        </Button>
      </div>

      <div className="flex flex-col">
        {generateInvitesFailureReason && (
          <Alert
            color="error"
            title="Uh oh."
            description={generateInvitesFailureReason.message}
          />
        )}
        <Button
          color="primary"
          onPress={() => generateInvites()}
          isDisabled={isGenerateInvitesPending}
        >
          Generate invites
        </Button>
      </div>

      <div className="flex flex-col">
        {cleanupStorageFailureReason && (
          <Alert
            color="error"
            title="Uh oh."
            description={cleanupStorageFailureReason.message}
          />
        )}
        <Button
          color="primary"
          onPress={() => cleanupStorage()}
          isDisabled={isCleanupStoragePending}
        >
          Cleanup storage
        </Button>
      </div>
    </DefaultLayout>
  );
}
